import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X, Globe, Check, Star, Zap, Shield, Clock, DollarSign, Languages } from 'lucide-react';
import GoogleTranslateService from '../services/googleTranslateService';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  background: ${props => props.theme.background};
  border-radius: 12px;
  width: 600px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from { 
      opacity: 0;
      transform: translateY(-20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  padding: 24px 24px 20px 24px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: ${props => props.theme.primary};
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.surface};
    color: ${props => props.theme.text};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Content = styled.div`
  padding: 24px;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 0 0 24px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const LanguageInfo = styled.div`
  background: ${props => props.theme.surface};
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  font-size: 14px;
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
`;

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const MethodCard = styled.div`
  border: 2px solid ${props => props.selected ? props.theme.primary : props.theme.border};
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? props.theme.primary + '08' : props.theme.surface};
  position: relative;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary + '08'};
  }
`;

const MethodHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const MethodInfo = styled.div`
  flex: 1;
`;

const MethodName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MethodIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.color || props.theme.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const MethodPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.free ? '#28a745' : props.theme.primary};
  background: ${props => props.free ? '#28a745' : props.theme.primary}15;
  padding: 4px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const MethodDescription = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.4;
`;

const MethodFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Feature = styled.span`
  background: ${props => props.theme.border};
  color: ${props => props.theme.text};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 10px;
    height: 10px;
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  background: ${props => 
    props.status === 'available' ? '#28a745' :
    props.status === 'configured' ? '#007bff' :
    props.status === 'missing' ? '#dc3545' :
    '#6c757d'
  };
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${props => props.theme.border};
`;

const Button = styled.button`
  flex: 1;
  padding: 12px 20px;
  border: 1px solid ${props => props.variant === 'primary' ? props.theme.primary : props.theme.border};
  border-radius: 6px;
  background: ${props => props.variant === 'primary' ? props.theme.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.text};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.primary + 'dd' : props.theme.surface};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LanguageSelection = styled.div`
  margin-bottom: 24px;
`;

const LanguageSelectHeader = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.theme.primary};
  }
`;

const LanguageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
  max-height: 120px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 8px;
  background: ${props => props.theme.surface};
`;

const LanguageOption = styled.button`
  padding: 8px 12px;
  border: 2px solid ${props => props.selected ? props.theme.primary : props.theme.border};
  border-radius: 4px;
  background: ${props => props.selected ? props.theme.primary + '08' : 'transparent'};
  color: ${props => props.theme.text};
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary + '08'};
  }
`;

const ContinueButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  margin-top: 16px;
  border: none;
  border-radius: 6px;
  background: ${props => props.theme.primary};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${props => props.theme.primary + 'dd'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TranslationMethodDialog = ({ 
  isOpen, 
  onClose, 
  onSelectMethod, 
  availableMethods,
  sourceLanguage = 'English',
  hasExistingTranslation = false
}) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('ar'); // Default to Arabic for Arabic users
  const [supportedLanguages, setSupportedLanguages] = useState([]);

  // Load supported languages when component mounts
  useEffect(() => {
    const loadLanguages = async () => {
      const googleService = new GoogleTranslateService();
      const languages = googleService.getSupportedLanguages();
      
      // Filter out the source language from target languages
      const sourceLanguageCode = getLanguageCode(sourceLanguage);
      const filteredLanguages = languages.filter(lang => lang.code !== sourceLanguageCode);
      
      setSupportedLanguages(filteredLanguages);
      
      // Set default language to Arabic if source is English, or English if source is Arabic
      if (sourceLanguageCode === 'en') {
        setSelectedLanguage('ar'); // Arabic for English source
      } else if (sourceLanguageCode === 'ar') {
        setSelectedLanguage('en'); // English for Arabic source  
      } else {
        setSelectedLanguage('en'); // Default to English for other sources
      }
    };
    
    loadLanguages();
  }, [sourceLanguage]);

  // Helper function to get language code from language name
  const getLanguageCode = (languageName) => {
    const googleService = new GoogleTranslateService();
    const languages = googleService.getSupportedLanguages();
    const language = languages.find(l => 
      l.name.toLowerCase() === languageName.toLowerCase() ||
      l.code.toLowerCase() === languageName.toLowerCase()
    );
    return language ? language.code : 'en';
  };

  if (!isOpen) return null;

  const methods = [
    {
      id: 'google-free',
      name: 'Google Translate (Free) - RECOMMENDED',
      description: 'Free translation service with good quality and high reliability',
      icon: <Globe />,
      color: '#4285f4',
      price: 'FREE',
      free: true,
      features: [
        { text: 'Completely Free', icon: <DollarSign /> },
        { text: '100+ Languages', icon: <Globe /> },
        { text: 'No Setup Required', icon: <Shield /> },
        { text: 'Most Reliable', icon: <Star /> }
      ],
      status: 'available',
      requirements: ['Internet Connection'],
      estimatedCost: 'Free'
    },
    {
      id: 'deepl-api',
      name: 'DeepL Professional',
      description: 'High-quality neural machine translation with context awareness',
      icon: <Globe />,
      color: '#0F2027',
      price: 'API Credits',
      free: false,
      features: [
        { text: 'Highest Quality', icon: <Star /> },
        { text: 'Fast Processing', icon: <Zap /> },
        { text: '31 Languages', icon: <Globe /> },
        { text: 'Context Aware', icon: <Shield /> }
      ],
      status: availableMethods.deepL ? 'configured' : 'missing',
      requirements: ['DeepL API Key'],
      estimatedCost: '~$0.02 per segment'
    },
    {
      id: 'azure-api',
      name: 'Azure Translator',
      description: 'Microsoft Azure translation service with enterprise features',
      icon: <Globe />,
      color: '#0078d4',
      price: 'API Credits',
      free: false,
      features: [
        { text: 'Enterprise Grade', icon: <Shield /> },
        { text: '90+ Languages', icon: <Globe /> },
        { text: 'Custom Models', icon: <Star /> },
        { text: 'Batch Processing', icon: <Zap /> }
      ],
      status: availableMethods.azure ? 'configured' : 'missing',
      requirements: ['Azure API Key'],
      estimatedCost: '~$0.01 per segment'
    },
    {
      id: 'local-libre',
      name: 'LibreTranslate (Local)',
      description: 'Free and open source translation running locally on your machine',
      icon: <Shield />,
      color: '#2e7d32',
      price: 'FREE',
      free: true,
      features: [
        { text: 'Completely Free', icon: <DollarSign /> },
        { text: 'Privacy Focused', icon: <Shield /> },
        { text: 'Offline Support', icon: <Clock /> },
        { text: 'No Data Sharing', icon: <Star /> }
      ],
      status: 'available',
      requirements: ['Docker or Local Setup'],
      estimatedCost: 'Free (Local Processing)'
    }
  ];

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
  };

  const handleContinue = () => {
    if (selectedMethod && selectedLanguage) {
      onSelectMethod(selectedMethod.id, selectedMethod, selectedLanguage);
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'configured': return 'Ready';
      case 'available': return 'Available';
      case 'missing': return 'Setup Required';
      case 'coming-soon': return 'Coming Soon';
      default: return 'Unknown';
    }
  };

  const selectedLanguageName = supportedLanguages.find(l => l.code === selectedLanguage)?.name || selectedLanguage;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <Globe />
            Choose Translation Method & Language
          </Title>
          <CloseButton onClick={onClose}>
            <X />
          </CloseButton>
        </Header>

        <Content>
          <Subtitle>
            Select translation method and target language. Choose Google Translate (Free) for best reliability.
          </Subtitle>
          
          <LanguageInfo>
            <strong>Source Language:</strong> {sourceLanguage}
            {selectedLanguage && (
              <>
                <br />
                <strong>Target Language:</strong> {selectedLanguageName}
              </>
            )}
          </LanguageInfo>
          
          {hasExistingTranslation && (
            <LanguageInfo style={{ background: '#e3f2fd', borderColor: '#2196f3' }}>
              <strong>📝 Note:</strong> You already have translations available. You can:
              <br />• Translate to a new language
              <br />• Re-translate existing content with better quality
              <br />• Use existing translations as source for another language
            </LanguageInfo>
          )}

          {/* Language Selection */}
          <LanguageSelection>
            <LanguageSelectHeader>
              <Languages />
              Available Target Languages
            </LanguageSelectHeader>
            <LanguageGrid>
              {supportedLanguages.map((language) => (
                <LanguageOption
                  key={language.code}
                  selected={selectedLanguage === language.code}
                  onClick={() => setSelectedLanguage(language.code)}
                >
                  {language.name}
                </LanguageOption>
              ))}
            </LanguageGrid>
          </LanguageSelection>

          <MethodGrid>
            {methods.map((method) => (
              <MethodCard
                key={method.id}
                selected={selectedMethod?.id === method.id}
                onClick={() => handleMethodSelect(method)}
              >
                <StatusBadge status={method.status}>
                  {getStatusText(method.status)}
                </StatusBadge>

                <MethodHeader>
                  <MethodInfo>
                    <MethodName>
                      <MethodIcon color={method.color}>
                        {method.icon}
                      </MethodIcon>
                      {method.name}
                    </MethodName>
                    <MethodDescription>
                      {method.description}
                    </MethodDescription>
                  </MethodInfo>
                  <MethodPrice free={method.free}>
                    {method.free && <DollarSign />}
                    {method.price}
                  </MethodPrice>
                </MethodHeader>

                <MethodFeatures>
                  {method.features.map((feature, index) => (
                    <Feature key={index}>
                      {feature.icon}
                      {feature.text}
                    </Feature>
                  ))}
                </MethodFeatures>

                <ContinueButton
                  disabled={!selectedMethod || !selectedLanguage}
                  onClick={handleContinue}
                >
                  <Check />
                  Translate to {selectedLanguageName}
                </ContinueButton>
              </MethodCard>
            ))}
          </MethodGrid>
        </Content>
      </Modal>
    </Overlay>
  );
};

export default TranslationMethodDialog; 